import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Award, TrendingUp, Calendar, Gift, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { parseLocalDate } from '../../lib/utils';
import { ReferralCard } from './ReferralCard';
import { TopArtistNameForm } from '../TopArtistSpotlight';

const API = '/api';

const BookingTracker = ({ userEmail }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [creditsToRedeem, setCreditsToRedeem] = useState(3);
  const [redeemable, setRedeemable] = useState([]);
  const [bookingId, setBookingId] = useState('');
  const [redeeming, setRedeeming] = useState(false);

  useEffect(() => {
    if (!showRedeemModal) return;
    axios.get(`${API}/credits/redeemable-bookings/${encodeURIComponent(userEmail)}`)
      .then(r => { setRedeemable(r.data); setBookingId(r.data[0]?.id || ''); })
      .catch(() => setRedeemable([]));
  }, [showRedeemModal, userEmail]);

  useEffect(() => {
    fetchStats();
    
    // Check for notifications
    const interval = setInterval(fetchStats, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [userEmail]);

  useEffect(() => {
    if (stats) {
      showNotifications(stats.needs_notification);
    }
  }, [stats]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/credits/stats/${userEmail}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default empty stats on error so component still renders
      setStats({
        hours_this_month: 0,
        total_hours: 0,
        bookings_this_month: 0,
        total_bookings: 0,
        credits_available: 0,
        credits_earned: 0,
        last_booking_date: null,
        average_weekly_bookings: 0,
        needs_notification: {
          booking_twice_weekly: false,
          booking_more_than_usual: false,
          studio_misses_you: false,
          milestone_reached: false,
          credits_earned: false
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const showNotifications = (notifications) => {
    if (notifications.booking_twice_weekly) {
      toast.success('🔥 You\'re on fire! Booking twice this week!', { duration: 5000 });
    }
    
    if (notifications.booking_more_than_usual) {
      toast.info('📈 You\'re booking more than usual! Keep up the momentum!', { duration: 5000 });
    }
    
    if (notifications.studio_misses_you) {
      toast('😢 We miss you! It\'s been a while since your last session.', { 
        duration: 7000,
        action: {
          label: 'Book Now',
          onClick: () => window.location.href = '#book'
        }
      });
    }
    
    if (notifications.milestone_reached) {
      toast.success(`🎉 Milestone! You've booked ${stats.total_hours} hours!`, { duration: 5000 });
    }
    
    if (notifications.credits_earned && stats.credits_available >= 3 && !sessionStorage.getItem('hq_redeem_toast')) {
      sessionStorage.setItem('hq_redeem_toast', '1');
      toast.success(`🎁 You have ${stats.credits_available} credits! Ready to redeem?`, { 
        duration: 7000,
        action: {
          label: 'Redeem',
          onClick: () => setShowRedeemModal(true)
        }
      });
    }
  };

  const handleRedeem = async () => {
    if (redeeming) return;
    setRedeeming(true);
    try {
      const response = await axios.post(`${API}/credits/apply-to-booking`, {
        email: userEmail,
        booking_id: bookingId,
        credits_to_redeem: creditsToRedeem
      }, { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` } });
      
      toast.success(response.data.message);
      setShowRedeemModal(false);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error redeeming credits:', error);
      toast.error(error.response?.data?.detail || 'Failed to redeem credits');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="p-4 bg-gray-900/50 border border-purple-500/20 rounded-lg">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <TrendingUp size={20} className="text-purple-400" />
            Your Stats
          </h3>
          <button
            onClick={fetchStats}
            className="text-xs text-purple-400 hover:text-purple-300"
          >
            Refresh
          </button>
        </div>

        {/* This Month's Hours */}
        <div className="bg-black/30 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-blue-400" />
              <span className="text-gray-300 text-sm">This Month</span>
            </div>
            <span className="text-2xl font-bold text-white">{stats.hours_this_month}h</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">{stats.bookings_this_month} bookings</p>
        </div>

        {/* Credits Available */}
        <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Award size={18} className="text-yellow-400" />
              <span className="text-gray-300 text-sm">Credits</span>
            </div>
            <span className="text-2xl font-bold text-yellow-400">{stats.credits_available}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">3 credits = 1 hour</span>
            {stats.credits_available >= 3 && (
              <button
                onClick={() => setShowRedeemModal(true)}
                className="text-yellow-400 hover:text-yellow-300 font-semibold"
              >
                Redeem →
              </button>
            )}
          </div>
          <div className="mt-2 pt-2 border-t border-yellow-500/20">
            <p className="text-xs text-gray-400">
              Next credit in {3 - (stats.total_hours % 3)} hours
            </p>
          </div>
        </div>

        <TopArtistNameForm />
        <ReferralCard userEmail={userEmail} />

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-black/30 rounded-lg p-2">
            <p className="text-xs text-gray-400">Total Hours</p>
            <p className="text-lg font-bold text-white">{stats.total_hours}h</p>
          </div>
          <div className="bg-black/30 rounded-lg p-2">
            <p className="text-xs text-gray-400">Avg/Week</p>
            <p className="text-lg font-bold text-white">{stats.average_weekly_bookings}</p>
          </div>
        </div>

        {/* Last Booking */}
        {stats.last_booking_date && (
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <Calendar size={12} />
            Last booking: {(() => { const d = parseLocalDate(String(stats.last_booking_date).slice(0, 10)); return d && !isNaN(d) ? d.toLocaleDateString() : '-'; })()}
          </div>
        )}
      </div>

      {/* Redeem Credits Modal */}
      {showRedeemModal && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/30 rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Gift className="text-yellow-400" size={24} />
                Redeem Credits
              </h3>
              <button
                onClick={() => setShowRedeemModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-200 text-sm mb-2">
                  You have <span className="font-bold text-xl">{stats.credits_available}</span> credits
                </p>
                <p className="text-gray-400 text-xs">
                  3 credits = 1 hour of studio time
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Apply free hours to which booking?
                </label>
                {redeemable.length === 0 ? (
                  <p data-testid="redeem-no-bookings" className="text-sm text-gray-400">
                    You have no upcoming bookings with a balance. Book a session first, then use your free hours at checkout or here.
                  </p>
                ) : (
                  <select
                    data-testid="redeem-booking-select"
                    value={bookingId}
                    onChange={(e) => setBookingId(e.target.value)}
                    className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                  >
                    {redeemable.map(b => (
                      <option key={b.id} value={b.id}>
                        {b.booking_number} · {b.date} {b.time} · {b.engineer_name} · ${b.balance_due.toFixed(2)} due
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Credits to Redeem (multiples of 3)
                </label>
                <input
                  type="number"
                  min="3"
                  step="3"
                  max={Math.floor(stats.credits_available / 3) * 3}
                  value={creditsToRedeem}
                  onChange={(e) => setCreditsToRedeem(parseInt(e.target.value))}
                  data-testid="redeem-credits-input"
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  = {Math.floor(creditsToRedeem / 3)} hour(s) of studio time
                </p>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={handleRedeem}
                  data-testid="redeem-submit-btn"
                  disabled={redeeming || !bookingId || creditsToRedeem < 3 || creditsToRedeem % 3 !== 0 || creditsToRedeem > stats.credits_available}
                  className="flex-1 bg-gradient-to-r from-yellow-600 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-yellow-700 hover:to-orange-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {redeeming ? 'Applying...' : 'Apply Free Hours'}
                </button>
                <button
                  onClick={() => setShowRedeemModal(false)}
                  className="px-4 py-3 text-gray-400 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default BookingTracker;
